/**
 * useAutoEscalation — hook qui surveille en continu les tâches en retard
 * et déclenche automatiquement leur escalade vers le N+1.
 *
 * Fréquence de scan : toutes les 5 minutes + 1 fois au montage.
 * Pour chaque tâche escaladée :
 *   1. Un tag 'escalated' est ajouté (évite les doublons)
 *   2. Un email mailto:? est ouvert (ou copié dans le presse-papier en fallback)
 *   3. Un toast visible dans l'interface annonce l'escalade
 */
import { useEffect, useState } from 'react';
import { useTaskStore } from '@store/taskStore';
import { useUserStore } from '@store/userStore';
import { useSettingsStore } from '@store/settingsStore';
import {
  buildEscalationEmail,
  escalatedTag,
  findTasksToEscalate,
  type EscalationDecision,
} from '@services/escalationEngine';
import { UniqueBridge } from '@services/uniqueBridge';

const SCAN_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export interface EscalationNotice {
  id: string;
  taskTitle: string;
  quadrant: string;
  daysOverdue: number;
  at: Date;
}

export function useAutoEscalation(): EscalationNotice[] {
  const [notices, setNotices] = useState<EscalationNotice[]>([]);

  useEffect(() => {
    const scan = async () => {
      const settings = useSettingsStore.getState();
      if (!settings.autoEscalationEnabled) return;
      if (!settings.supervisor.email) return;

      const { tasks, updateTask } = useTaskStore.getState();
      const profile = useUserStore.getState().profile;
      const senderName = profile
        ? `${profile.firstName} ${profile.lastName}`.trim()
        : 'Utilisateur UNIQUE';

      const decisions: EscalationDecision[] = findTasksToEscalate(
        tasks,
        settings.escalationDelays,
      );
      if (decisions.length === 0) return;

      for (const decision of decisions) {
        // 1. Tag d'escalade pour éviter les doublons
        updateTask(decision.task.id, {
          tags: [...decision.task.tags, escalatedTag()],
          status: decision.task.status === 'todo' ? 'blocked' : decision.task.status,
        });

        // 2. Construction de l'email et ouverture
        const mail = buildEscalationEmail(decision, settings.supervisor, senderName);
        const params = new URLSearchParams();
        params.set('subject', mail.subject);
        params.set('body', mail.body);
        const uri = `mailto:${encodeURIComponent(mail.to)}?${params.toString()}`;
        void UniqueBridge.openApp(uri);

        // 3. Notice pour l'UI
        setNotices((prev) => [
          ...prev.slice(-4),
          {
            id: decision.task.id,
            taskTitle: decision.task.title,
            quadrant: decision.quadrant,
            daysOverdue: decision.daysOverdue,
            at: new Date(),
          },
        ]);
      }
    };

    void scan();
    const id = window.setInterval(() => void scan(), SCAN_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return notices;
}
